import React, { useMemo, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Group,
  Text,
  Alert,
  FileButton,
  Stack,
  Badge,
  Loader,
  TextInput,
  Textarea,
  Switch,
  Checkbox,
} from "@mantine/core";
import * as XLSX from "xlsx";
import { z } from "zod";
import { useProjectId } from "../hooks/useProjectId";
import {
  usePostMultiplacerAirflowsImportMutation,
  usePostMultiplacerRoomsQuery,
} from "../../openapi";

// ---- Zod-skjema for radene i Excel ----
const ZRow = z.object({
  roomNumber: z.string().trim().min(1, "Romnummer er påkrevd"),
  supply: z.coerce.number().min(0, "Tilluft må være ≥ 0"),
  exhaust: z.coerce.number().min(0, "Avtrekk må være ≥ 0"),
});
type ImportRow = z.infer<typeof ZRow>;

type ParseError = { row: number; field: keyof ImportRow; message: string; value: unknown };

// Heuristikk for romnummer som "01.01.001"
const ROOM_REGEX_STRICT = /^\d{2}\.\d{2}\.\d{3}$/;
const ROOM_REGEX_LOOSE = /^\d{1,3}\.\d{1,3}\.\d{1,4}$/;

function looksLikeRoomNumber(v: unknown) {
  const s = String(v ?? "").trim();
  if (!s) return false;
  return ROOM_REGEX_STRICT.test(s) || ROOM_REGEX_LOOSE.test(s);
}

function isBlank(v: unknown) {
  return v == null || String(v).trim() === "";
}

// Tolerant tallparser. Tomt => 0 (sett blankAsZero=false hvis du vil kreve verdi)
function parseNumberLoose(v: unknown, blankAsZero = true): number {
  if (isBlank(v)) return blankAsZero ? 0 : NaN;
  if (typeof v === "number") return v;
  const s = String(v).trim().replace(/\s/g, "");
  if (s.includes(",") && s.includes(".")) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    const decimalSep = lastComma > lastDot ? "," : ".";
    const normalized = s.replace(/[,.](?=.*[,.])/g, "").replace(decimalSep, ".");
    return Number(normalized);
  }
  return Number(s.replace(",", "."));
}

// Robust uthenting av romnumre fra API-respons uansett struktur
function extractRoomNumberSet(roomsData: any): Set<string> {
  const set = new Set<string>();
  const tryAdd = (val: any) => {
    if (typeof val === "string" && looksLikeRoomNumber(val)) set.add(val.trim());
  };
  const scan = (obj: any) => {
    if (!obj) return;
    if (Array.isArray(obj)) return obj.forEach(scan);
    if (typeof obj === "object") {
      Object.keys(obj).forEach((k) => tryAdd(k)); // nøkler kan også være romnummer
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === "string" && /room|number|name|id|rom/i.test(k)) tryAdd(v);
        if (typeof v === "object") scan(v);
      }
      return;
    }
    tryAdd(obj);
  };
  scan(roomsData);
  return set;
}

export function AirflowExcelImportSmart({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  // Prosjekt og romliste
  const projectId = useProjectId();
  const [search, setSearch] = useState("");
  const {
    data: rooms = {},
    isLoading: isRoomsLoading,
    isError: isRoomsError,
  } = usePostMultiplacerRoomsQuery(
    { body: { projectNo: projectId } },
    { skip: !opened }
  );

  const roomSet = useMemo(() => extractRoomNumberSet(rooms), [rooms]);

  // Import-state
  const [preview, setPreview] = useState<ImportRow[]>([]);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [errors, setErrors] = useState<ParseError[]>([]);
  const [unknownRooms, setUnknownRooms] = useState<string[]>([]);
  const [allowUnknown, setAllowUnknown] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [sourceFileName, setSourceFileName] = useState("");
  const [comment, setComment] = useState("");

  const hasErrors = errors.length > 0;
  const hasUnknown = unknownRooms.length > 0;

  // RTK-mutasjon
  const [
    importAirflows,
    { isLoading: isImporting, isError: isImportError, error: importError, data: importResult },
  ] = usePostMultiplacerAirflowsImportMutation();

  async function handleFile(file: File | null) {
    if (!file) return;
    setSourceFileName(file.name);
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as any[][];

    if (!rows.length) {
      setPreview([]);
      setImportRows([]);
      setErrors([{ row: 0, field: "roomNumber", message: "Filen var tom", value: "" }]);
      setUnknownRooms([]);
      return;
    }

    // Om første rad IKKE ser ut som data → betrakt som header og hopp over den
    const firstRowLooksLikeData = looksLikeRoomNumber(rows[0]?.[0]);
    const dataRows = firstRowLooksLikeData ? rows : rows.slice(1);

    // Faste posisjoner: 0=Romnummer, 1=Tilluft, 2=Avtrekk
    const raw = dataRows
      .filter((r) => r && (r[0] != null || r[1] != null || r[2] != null))
      .map((r) => ({
        roomNumber: String(r[0] ?? "").trim(),
        supply: parseNumberLoose(r[1], true),
        exhaust: parseNumberLoose(r[2], true),
      }));

    const nextErrors: ParseError[] = [];
    const validRows: ImportRow[] = [];

    raw.forEach((r, i) => {
      const parsed = ZRow.safeParse(r);
      if (parsed.success) {
        validRows.push(parsed.data);
      } else {
        parsed.error.issues.forEach((iss) => {
          nextErrors.push({
            row: (firstRowLooksLikeData ? 0 : 1) + i + 1, // Excel-radnummer (1-basert)
            field: (iss.path[0] as keyof ImportRow) ?? "roomNumber",
            message: iss.message,
            value: (r as any)[iss.path[0] as keyof ImportRow],
          });
        });
      }
    });

    setPreview(validRows.slice(0, 200)); // forhåndsvis første 200
    setImportRows(validRows);
    setErrors(nextErrors);

    // Kryssjekk mot prosjektrom (hvis tilgjengelig)
    if (roomSet.size > 0) {
      const unknown = Array.from(
        new Set(validRows.map((r) => r.roomNumber).filter((rn) => !roomSet.has(rn)))
      ).sort();
      setUnknownRooms(unknown);
    } else {
      setUnknownRooms([]); // ikke blokker hvis vi ikke fikk romliste
    }
  }

  const filteredUnknown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return unknownRooms;
    return unknownRooms.filter((r) => r.toLowerCase().includes(q));
  }, [unknownRooms, search]);

  const importDisabled =
    isImporting ||
    importRows.length === 0 ||
    hasErrors ||
    isRoomsLoading ||
    (!allowUnknown && hasUnknown);

  const handleImport = async () => {
    // Map til server-DTO: supplyAirFlow / returnAirFlow
    const items = importRows.map((r) => ({
      roomNumber: r.roomNumber,
      supplyAirFlow: r.supply,
      returnAirFlow: r.exhaust,
    }));

    const res = await importAirflows({
      body: {
        projectNo: projectId,
        items,
        dryRun,
        sourceFileName,
        comment: comment.trim() || undefined,
      },
    }).unwrap();

    // Ved faktisk import (ikke dryRun) kan du velge å lukke automatisk:
    if (!dryRun) {
      onClose();
      // reset lokalt state
      setPreview([]);
      setImportRows([]);
      setErrors([]);
      setUnknownRooms([]);
      setSearch("");
      setAllowUnknown(false);
      setDryRun(false);
      setSourceFileName("");
      setComment("");
    }
    // Hvis dryRun: behold modalen å vise resultatet under.
    return res;
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Excelimport: Tilluft/Avtrekk" size="lg">
      <Stack gap="md">
        <Text size="sm">
          Kolonnerekkefølge må være: <b>1:</b> Romnummer, <b>2:</b> Tilluft (m³/h), <b>3:</b>{" "}
          Avtrekk (m³/h). Header-tekst spiller ingen rolle.
        </Text>

        <Table withTableBorder withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>1: Romnummer</Table.Th>
              <Table.Th>2: Tilluft (m³/h)</Table.Th>
              <Table.Th>3: Avtrekk (m³/h)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>01.01.001</Table.Td>
              <Table.Td>220</Table.Td>
              <Table.Td>0</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>01.01.002</Table.Td>
              <Table.Td>220</Table.Td>
              <Table.Td>0</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Badge color="blue" variant="light">
              Kol 1: Rom
            </Badge>
            <Badge color="green" variant="light">
              Kol 2: Tilluft
            </Badge>
            <Badge color="teal" variant="light">
              Kol 3: Avtrekk
            </Badge>
          </Group>

          <Group>
            {isRoomsLoading && (
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="xs" c="dimmed">
                  Laster romliste…
                </Text>
              </Group>
            )}
            {isRoomsError && (
              <Badge
                color="orange"
                variant="light"
                title="Klarte ikke hente rom; import er mulig men ukjente rom kan ikke sjekkes."
              >
                Romliste utilgjengelig
              </Badge>
            )}
            <FileButton onChange={handleFile} accept=".xlsx,.xls,.csv">
              {(props) => <Button {...props}>Velg fil</Button>}
            </FileButton>
          </Group>
        </Group>

        {sourceFileName && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Valgt fil: {sourceFileName}
            </Text>
            <Textarea
              label="Kommentar"
              placeholder="Valgfri kommentar for denne Excel-importen"
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        )}

        {/* Feil i Excel-data */}
        {errors.length > 0 && (
          <Alert variant="light" color="red" title={`Feil i data (${errors.length})`}>
            <Text size="sm">
              Riktig kolonnerekkefølge, men noen rader inneholder feil. Rette i Excel og last opp på
              nytt.
            </Text>
            <Table mt="xs" withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rad</Table.Th>
                  <Table.Th>Felt</Table.Th>
                  <Table.Th>Verdi</Table.Th>
                  <Table.Th>Melding</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {errors.slice(0, 10).map((e, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{e.row}</Table.Td>
                    <Table.Td>{e.field}</Table.Td>
                    <Table.Td>{String(e.value ?? "")}</Table.Td>
                    <Table.Td>{e.message}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {errors.length > 10 && (
              <Text size="xs" c="dimmed" mt={4}>
                Viser de 10 første feilene.
              </Text>
            )}
          </Alert>
        )}

        {/* Ukjente rom i prosjektet */}
        {unknownRooms.length > 0 && (
          <Alert
            variant="light"
            color={allowUnknown ? "yellow" : "red"}
            title={`Ukjente rom i prosjektet (${unknownRooms.length})`}
          >
            <Text size="sm" mb="xs">
              Disse romnumrene finnes ikke i prosjektet. Rette i Excel eller opprett rom. Du kan
              likevel importere kjente rom ved å krysse av «Tillat import med ukjente rom».
            </Text>
            <Group mb="xs" justify="space-between" wrap="nowrap">
              <TextInput
                placeholder="Søk i ukjente rom…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ width: 260 }}
              />
              <Checkbox
                checked={allowUnknown}
                onChange={(e) => setAllowUnknown(e.currentTarget.checked)}
                label="Tillat import med ukjente rom"
              />
            </Group>
            <Table withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Romnummer</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredUnknown.slice(0, 50).map((r) => (
                  <Table.Tr key={r}>
                    <Table.Td>{r}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {filteredUnknown.length > 50 && (
              <Text size="xs" c="dimmed" mt={4}>
                Viser første 50. Bruk søk for å filtrere.
              </Text>
            )}
          </Alert>
        )}

        {/* Forhåndsvisning */}
        {preview.length > 0 && (
          <>
            <Group justify="space-between" align="center">
              <Text fw={600}>
                Forhåndsvisning (første {preview.length} av {importRows.length} rader)
              </Text>
              <Switch
                checked={dryRun}
                onChange={(e) => setDryRun(e.currentTarget.checked)}
                label="Dry run (ingen skriving til database)"
              />
            </Group>
            <Table withColumnBorders striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Romnummer</Table.Th>
                  <Table.Th>Tilluft (m³/h)</Table.Th>
                  <Table.Th>Avtrekk (m³/h)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.map((r, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{r.roomNumber}</Table.Td>
                    <Table.Td>{r.supply}</Table.Td>
                    <Table.Td>{r.exhaust}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* Resultat fra import (viser kun hvis dryRun eller feil) */}
        {importResult && dryRun && (
          <Alert variant="light" color="blue" title="Dry run resultat">
            <Text size="sm">
              Mottatt: {importResult.counts.received} &nbsp;|&nbsp; Dedup:{" "}
              {importResult.counts.deduplicated} &nbsp;|&nbsp; Matchet:{" "}
              {importResult.counts.matched} &nbsp;|&nbsp; Ville blitt oppdatert:{" "}
              {importResult.counts.updated} &nbsp;|&nbsp; Uendret: {importResult.counts.unchanged}{" "}
              &nbsp;|&nbsp; Ukjente: {importResult.counts.unknown}
            </Text>
            {importResult.unknownRooms?.length > 0 && (
              <>
                <Text size="sm" mt="xs">
                  Ukjente (server):
                </Text>
                <Text size="xs" c="dimmed">
                  {importResult.unknownRooms.slice(0, 50).join(", ")}
                  {importResult.unknownRooms.length > 50 ? " …" : ""}
                </Text>
              </>
            )}
          </Alert>
        )}

        {isImportError && (
          <Alert variant="light" color="red" title="Import feilet">
            <Text size="sm">
              {String(
                (importError as any)?.data?.message ?? (importError as any)?.error ?? "Ukjent feil"
              )}
            </Text>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Avbryt
          </Button>
          <Button disabled={importDisabled} loading={isImporting} onClick={handleImport}>
            {dryRun ? "Kjør dry run" : "Importer"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
